import { forwardRef, type HTMLAttributes } from "react";
import s from "./card.module.scss";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Background variant. "muted" sits on --color-bg-muted (for inner cards). */
  variant?: "default" | "muted";
  /** Adds pointer cursor + hover lift + hover accent border. */
  interactive?: boolean;
  /** Hover border colour when `interactive`. Default: primary. */
  accent?: "primary" | "turquoise" | "info" | "success" | "warning";
}

const ACCENT_CLASS: Record<NonNullable<CardProps["accent"]>, string> = {
  primary: "",
  turquoise: s.accentTurquoise ?? "",
  info: s.accentInfo ?? "",
  success: s.accentSuccess ?? "",
  warning: s.accentWarning ?? "",
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant, interactive, accent, className, ...props }, ref) => {
    const cls = [
      s.card,
      variant === "muted" ? s.muted : "",
      interactive ? s.interactive : "",
      accent ? ACCENT_CLASS[accent] : "",
      className ?? "",
    ].filter(Boolean).join(" ");
    return <div ref={ref} className={cls} {...props} />;
  },
);
Card.displayName = "Card";

export function CardHeader({ className, title, children, ...props }: HTMLAttributes<HTMLDivElement> & { title?: string }) {
  return (
    <div className={`${s.header} ${className ?? ""}`} {...props}>
      {title ? <h3 className={s.headerTitle}>{title}</h3> : null}
      {children}
    </div>
  );
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`${s.content} ${className ?? ""}`} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`${s.footer} ${className ?? ""}`} {...props} />;
}
